import { useEffect, useMemo } from 'react';

import Box from '@mui/material/Box';
import { Map, Marker, useMap } from '@vis.gl/react-google-maps';

import { ShortlistItemProvider } from '@karehero/models';

// Roughly the centre of the UK — shown when there's nothing to plot yet.
const DEFAULT_CENTER = { lat: 54.5, lng: -3 };
const DEFAULT_ZOOM = 6;

const SEARCH_LOCATION_ICON =
  'https://maps.google.com/mapfiles/ms/icons/blue-dot.png';

interface MapPoint {
  lat: number;
  lng: number;
}

const FitToPoints = ({ points }: { points: MapPoint[] }) => {
  const map = useMap();

  useEffect(() => {
    if (!map || points.length === 0) return;

    if (points.length === 1) {
      map.setCenter(points[0]);
      map.setZoom(13);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    points.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds);
  }, [map, points]);

  return null;
};

interface MapViewProps {
  results: ShortlistItemProvider[];
  searchLocation?: { lat: number; lon: number } | null;
  onProviderClick: (providerID: string) => void;
}

export const MapView = ({
  results,
  searchLocation,
  onProviderClick,
}: MapViewProps) => {
  const searchLat = searchLocation?.lat;
  const searchLon = searchLocation?.lon;

  const { points, searchPoint } = useMemo(() => {
    const sp =
      searchLat != null && searchLon != null
        ? { lat: searchLat, lng: searchLon }
        : null;
    return {
      points: [
        ...results.map((provider) => ({
          lat: provider.location.lat,
          lng: provider.location.lon,
        })),
        ...(sp ? [sp] : []),
      ],
      searchPoint: sp,
    };
  }, [results, searchLat, searchLon]);

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        borderRadius: '12px',
        overflow: 'hidden',
      }}
    >
      <Map
        defaultCenter={DEFAULT_CENTER}
        defaultZoom={DEFAULT_ZOOM}
        gestureHandling='greedy'
        disableDefaultUI={false}
      >
        <FitToPoints points={points} />
        {searchPoint && (
          <Marker position={searchPoint} icon={SEARCH_LOCATION_ICON} />
        )}
        {results.map((provider) => (
          <Marker
            key={provider.regulatoryId}
            position={{
              lat: provider.location.lat,
              lng: provider.location.lon,
            }}
            title={provider.name}
            onClick={() => onProviderClick(provider.regulatoryId)}
          />
        ))}
      </Map>
    </Box>
  );
};

export default MapView;
